import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FiUser,
  FiMapPin,
  FiPlus,
  FiTrash2,
  FiEdit,
  FiCheck,
  FiX,
  FiHome,
  FiBriefcase,
  FiArrowLeft
} from 'react-icons/fi';
import './ProfilePage.css';
import { API_BASE_URL } from '../../config';

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand",
  "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands",
  "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu", "Delhi",
  "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
];

export default function ProfilePage({
  currentUser,
  setCurrentUser,
  triggerNotification,
  onBack
}) {
  const [activeSubTab, setActiveSubTab] = useState('personal'); // 'personal' | 'addresses'
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addresses, setAddresses] = useState([]);

  // Personal Info Form
  const [personalForm, setPersonalForm] = useState({
    first_name: '',
    last_name: '',
    phone_number: '',
    email: ''
  });

  // Address Modal Form
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [addressForm, setAddressForm] = useState({
    fullName: '',
    phone: '',
    altPhone: '',
    pincode: '',
    addressLine1: '',
    addressLine2: '',
    landmark: '',
    city: '',
    state: 'Karnataka',
    addressType: 'Home',
    isDefault: false,
    isBusiness: false,
    companyName: '',
    gstin: ''
  });

  useEffect(() => {
    if (currentUser) {
      setPersonalForm({
        first_name: currentUser.first_name || '',
        last_name: currentUser.last_name || '',
        phone_number: currentUser.phone_number || '',
        email: currentUser.email || ''
      });
    }
  }, [currentUser]);

  useEffect(() => {
    if (activeSubTab === 'addresses') {
      fetchAddresses();
    }
  }, [activeSubTab]);

  const fetchAddresses = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/addresses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddresses(res.data.addresses || []);
    } catch (err) {
      console.error(err);
      triggerNotification('Failed to load saved addresses.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) return;

    setSaving(true);
    try {
      const res = await axios.put(`${API_BASE_URL}/auth/profile`, {
        first_name: personalForm.first_name,
        last_name: personalForm.last_name,
        phone_number: personalForm.phone_number
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      triggerNotification(res.data.message || 'Profile updated successfully.');
      if (setCurrentUser) {
        setCurrentUser(prev => ({
          ...prev,
          first_name: personalForm.first_name,
          last_name: personalForm.last_name,
          phone_number: personalForm.phone_number
        }));
      }
    } catch (err) {
      console.error(err);
      triggerNotification(err.response?.data?.message || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAddAddress = () => {
    setIsEditingAddress(false);
    setEditingAddressId(null);
    setAddressForm({
      fullName: '',
      phone: '',
      altPhone: '',
      pincode: '',
      addressLine1: '',
      addressLine2: '',
      landmark: '',
      city: '',
      state: 'Karnataka',
      addressType: 'Home',
      isDefault: false,
      isBusiness: false,
      companyName: '',
      gstin: ''
    });
    setShowAddressModal(true);
  };

  const handleOpenEditAddress = (addr) => {
    setIsEditingAddress(true);
    setEditingAddressId(addr.id);
    setAddressForm({
      fullName: addr.fullName || addr.full_name || '',
      phone: addr.phone || '',
      altPhone: addr.altPhone || addr.alt_phone || '',
      pincode: addr.pincode || '',
      addressLine1: addr.addressLine1 || addr.address_line1 || '',
      addressLine2: addr.addressLine2 || addr.address_line2 || '',
      landmark: addr.landmark || '',
      city: addr.city || '',
      state: addr.state || 'Karnataka',
      addressType: addr.addressType || addr.address_type || 'Home',
      isDefault: addr.isDefault === 1 || addr.isDefault === true || addr.is_default === 1 || addr.is_default === true,
      isBusiness: !!(addr.companyName || addr.company_name),
      companyName: addr.companyName || addr.company_name || '',
      gstin: addr.gstin || ''
    });
    setShowAddressModal(true);
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) return;

    if (!addressForm.fullName || !addressForm.phone || !addressForm.pincode || !addressForm.addressLine1 || !addressForm.addressLine2 || !addressForm.city) {
      triggerNotification('Please fill in all required fields.');
      return;
    }

    const payload = {
      fullName: addressForm.fullName,
      phone: addressForm.phone,
      altPhone: addressForm.altPhone || null,
      pincode: addressForm.pincode,
      addressLine1: addressForm.addressLine1,
      addressLine2: addressForm.addressLine2,
      landmark: addressForm.landmark || null,
      city: addressForm.city,
      state: addressForm.state,
      addressType: addressForm.addressType,
      isDefault: addressForm.isDefault ? 1 : 0,
      companyName: addressForm.isBusiness ? addressForm.companyName : null,
      gstin: addressForm.isBusiness ? addressForm.gstin : null
    };

    setSaving(true);
    try {
      if (isEditingAddress) {
        await axios.put(`${API_BASE_URL}/addresses/${editingAddressId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        triggerNotification('Address updated successfully.');
      } else {
        await axios.post(`${API_BASE_URL}/addresses`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        triggerNotification('Address saved successfully.');
      }
      setShowAddressModal(false);
      fetchAddresses();
    } catch (err) {
      console.error(err);
      triggerNotification(err.response?.data?.message || 'Failed to save address.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAddress = async (id) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      await axios.delete(`${API_BASE_URL}/addresses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      triggerNotification('Address deleted successfully.');
      fetchAddresses();
    } catch (err) {
      console.error(err);
      triggerNotification('Failed to delete address.');
    }
  };

  const handleSetDefault = async (id) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      await axios.put(`${API_BASE_URL}/addresses/${id}/default`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      triggerNotification('Default address updated.');
      fetchAddresses();
    } catch (err) {
      console.error(err);
      triggerNotification('Failed to set default address.');
    }
  };

  return (
    <div className="profile-page-wrapper">
      <div className="profile-container">
        
        {/* Profile Header */}
        <header className="profile-header">
          <button className="profile-back-btn" onClick={onBack}>
            <FiArrowLeft /> Back
          </button>
          <div className="profile-title-block">
            <h2>My Profile & Settings</h2>
            <p>Manage your account settings, shipping info, and business details</p>
          </div>
        </header>

        {/* Profile Grid Layout (Sidebar + Content) */}
        <div className="profile-grid-body">
          <aside className="profile-sidebar-nav">
            <button 
              className={`profile-tab-nav-btn ${activeSubTab === 'personal' ? 'active' : ''}`}
              onClick={() => setActiveSubTab('personal')}
            >
              <FiUser /> Personal Details
            </button>
            <button 
              className={`profile-tab-nav-btn ${activeSubTab === 'addresses' ? 'active' : ''}`}
              onClick={() => setActiveSubTab('addresses')}
            >
              <FiMapPin /> Saved Addresses ({addresses.length || (currentUser ? '...' : 0)})
            </button>
          </aside>

          {/* Tab Contents */}
          <main className="profile-main-content">
            
            {/* PERSONAL INFO TAB */}
            {activeSubTab === 'personal' && (
              <div className="personal-details-view animate-fade-in">
                <form onSubmit={handleUpdateProfile} className="personal-info-form">
                  <div className="profile-card">
                    <div className="card-header">
                      <h3>Account Information</h3>
                    </div>
                    
                    <div className="form-grid">
                      <div className="input-group">
                        <label>First Name *</label>
                        <input 
                          type="text" 
                          required
                          value={personalForm.first_name}
                          onChange={e => setPersonalForm({ ...personalForm, first_name: e.target.value })}
                          placeholder="First Name"
                        />
                      </div>
                      <div className="input-group">
                        <label>Last Name *</label>
                        <input 
                          type="text" 
                          required
                          value={personalForm.last_name}
                          onChange={e => setPersonalForm({ ...personalForm, last_name: e.target.value })}
                          placeholder="Last Name"
                        />
                      </div>
                      <div className="input-group">
                        <label>Email Address</label>
                        <input 
                          type="email" 
                          disabled
                          value={personalForm.email}
                          placeholder="Email Address"
                          title="Email address cannot be changed."
                        />
                        <span className="input-helper-text">Registered email address (cannot be edited)</span>
                      </div>
                      <div className="input-group">
                        <label>Phone Number</label>
                        <input 
                          type="tel" 
                          value={personalForm.phone_number}
                          onChange={e => setPersonalForm({ ...personalForm, phone_number: e.target.value })}
                          placeholder="10-digit mobile number"
                        />
                      </div>
                    </div>

                    <div className="form-actions">
                      <button type="submit" className="save-profile-btn" disabled={saving}>
                        {saving ? 'Saving...' : 'Save Profile Changes'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            )}

            {/* ADDRESSES TAB */}
            {activeSubTab === 'addresses' && (
              <div className="addresses-view animate-fade-in">
                <div className="addresses-header-actions">
                  <h3>Delivery Profiles</h3>
                  <button className="add-address-trigger-btn" onClick={handleOpenAddAddress}>
                    <FiPlus /> Add New Address
                  </button>
                </div>

                {loading ? (
                  <div className="profile-loading-spinner">
                    <div className="spinner"></div>
                    <span>Fetching address profiles...</span>
                  </div>
                ) : addresses.length === 0 ? (
                  <div className="empty-addresses-state">
                    <FiMapPin className="empty-pin-icon" />
                    <h4>No Saved Addresses</h4>
                    <p>You haven't saved any addresses yet. Add one to speed up checkout.</p>
                    <button className="empty-add-btn" onClick={handleOpenAddAddress}>
                      <FiPlus /> Add Address Now
                    </button>
                  </div>
                ) : (
                  <div className="addresses-grid">
                    {addresses.map(addr => {
                      const isAddrDefault = addr.isDefault === 1 || addr.isDefault === true || addr.is_default === 1 || addr.is_default === true;
                      return (
                        <div key={addr.id} className={`address-profile-card ${isAddrDefault ? 'default' : ''}`}>
                          {isAddrDefault && (
                            <div className="default-badge-label">
                              <FiCheck /> Default
                            </div>
                          )}
                          <header className="address-card-head">
                            <span className={`address-category-tag ${(addr.addressType || addr.address_type) === 'Work' ? 'work' : 'home'}`}>
                              {(addr.addressType || addr.address_type) === 'Work' ? '💼 Work' : '🏠 Home'}
                            </span>
                            <div className="address-card-actions">
                              <button className="action-btn edit" onClick={() => handleOpenEditAddress(addr)} title="Edit Address"><FiEdit /></button>
                              <button className="action-btn delete" onClick={() => handleDeleteAddress(addr.id)} title="Delete Address"><FiTrash2 /></button>
                            </div>
                          </header>
                          
                          <div className="address-card-body">
                            <h4 className="receiver-name">{addr.fullName || addr.full_name}</h4>
                            
                            {/* Business Info Display */}
                            {(addr.companyName || addr.company_name) && (
                              <div className="business-info-display">
                                🏢 <strong>{addr.companyName || addr.company_name}</strong>
                                {addr.gstin && <span className="gstin-val"> (GSTIN: {addr.gstin})</span>}
                              </div>
                            )}

                            <p className="street-lines">{addr.addressLine1 || addr.address_line1}, {addr.addressLine2 || addr.address_line2}</p>
                            {addr.landmark && <p className="landmark-line">Landmark: {addr.landmark}</p>}
                            <p className="city-pincode">{addr.city}, {addr.state} - <strong>{addr.pincode}</strong></p>
                            <p className="phone-line">📞 {addr.phone}</p>
                            {addr.altPhone || addr.alt_phone ? <p className="phone-line alt">📞 {addr.altPhone || addr.alt_phone} (Alt)</p> : null}
                          </div>

                          {!isAddrDefault && (
                            <footer className="address-card-foot">
                              <button className="set-default-action-btn" onClick={() => handleSetDefault(addr.id)}>
                                Set as Default
                              </button>
                            </footer>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

          </main>
        </div>
      </div>

      {/* ADDRESS MODAL FORM */}
      {showAddressModal && (
        <div className="profile-modal-overlay" onClick={() => setShowAddressModal(false)}>
          <div className="profile-modal-card animate-scale-up" onClick={e => e.stopPropagation()}>
            <header className="modal-header">
              <h3>{isEditingAddress ? '📝 Edit Address' : '🏠 Add New Delivery Profile'}</h3>
              <button className="modal-close-btn" onClick={() => setShowAddressModal(false)}>
                <FiX />
              </button>
            </header>

            <form onSubmit={handleAddressSubmit} className="modal-address-form">
              <div className="modal-scroll-area">
                <div className="form-double-row">
                  <div className="input-group">
                    <label>Receiver Full Name *</label>
                    <input 
                      type="text" 
                      required
                      value={addressForm.fullName}
                      onChange={e => setAddressForm({ ...addressForm, fullName: e.target.value })}
                      placeholder="e.g. John Doe"
                    />
                  </div>
                  <div className="input-group">
                    <label>Contact Phone Number *</label>
                    <input 
                      type="tel" 
                      required
                      value={addressForm.phone}
                      onChange={e => setAddressForm({ ...addressForm, phone: e.target.value })}
                      placeholder="10-digit phone"
                    />
                  </div>
                </div>

                <div className="form-double-row">
                  <div className="input-group">
                    <label>Alternative Phone (Optional)</label>
                    <input 
                      type="tel" 
                      value={addressForm.altPhone}
                      onChange={e => setAddressForm({ ...addressForm, altPhone: e.target.value })}
                      placeholder="Alt number"
                    />
                  </div>
                  <div className="input-group">
                    <label>Pincode / Zipcode *</label>
                    <input 
                      type="text" 
                      required
                      value={addressForm.pincode}
                      onChange={e => setAddressForm({ ...addressForm, pincode: e.target.value })}
                      placeholder="6-digit ZIP"
                    />
                  </div>
                </div>

                <div className="input-group">
                  <label>Address Line 1 (House, Apartment, Suite) *</label>
                  <input 
                    type="text" 
                    required
                    value={addressForm.addressLine1}
                    onChange={e => setAddressForm({ ...addressForm, addressLine1: e.target.value })}
                    placeholder="Flat/House number, building name"
                  />
                </div>

                <div className="input-group">
                  <label>Address Line 2 (Area, Street, Sector, Village) *</label>
                  <input 
                    type="text" 
                    required
                    value={addressForm.addressLine2}
                    onChange={e => setAddressForm({ ...addressForm, addressLine2: e.target.value })}
                    placeholder="Street name, area details"
                  />
                </div>

                <div className="form-triple-row">
                  <div className="input-group">
                    <label>Landmark (Optional)</label>
                    <input 
                      type="text" 
                      value={addressForm.landmark}
                      onChange={e => setAddressForm({ ...addressForm, landmark: e.target.value })}
                      placeholder="e.g. Near Big Temple"
                    />
                  </div>
                  <div className="input-group">
                    <label>City *</label>
                    <input 
                      type="text" 
                      required
                      value={addressForm.city}
                      onChange={e => setAddressForm({ ...addressForm, city: e.target.value })}
                      placeholder="e.g. Bengaluru"
                    />
                  </div>
                  <div className="input-group">
                    <label>State *</label>
                    <select
                      value={addressForm.state}
                      onChange={e => setAddressForm({ ...addressForm, state: e.target.value })}
                    >
                      {INDIAN_STATES.map(st => (
                        <option key={st} value={st}>{st}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-options-row">
                  <div className="input-group">
                    <label>Address Category:</label>
                    <div className="address-type-selection">
                      <button
                        type="button"
                        className={`type-btn ${addressForm.addressType === 'Home' ? 'active' : ''}`}
                        onClick={() => setAddressForm({ ...addressForm, addressType: 'Home' })}
                      >
                        Home
                      </button>
                      <button
                        type="button"
                        className={`type-btn ${addressForm.addressType === 'Work' ? 'active' : ''}`}
                        onClick={() => setAddressForm({ ...addressForm, addressType: 'Work' })}
                      >
                        Work
                      </button>
                    </div>
                  </div>

                  <label className="checkbox-label" style={{ userSelect: 'none' }}>
                    <input 
                      type="checkbox" 
                      checked={addressForm.isDefault}
                      onChange={e => setAddressForm({ ...addressForm, isDefault: e.target.checked })}
                    />
                    Set as default address
                  </label>
                </div>

                {/* BUSINESS BUY INVOICE TOGGLE */}
                <div className="business-buy-box">
                  <label className="checkbox-label business-toggle" style={{ margin: 0, fontWeight: 700, color: '#2e7d32' }}>
                    <input 
                      type="checkbox" 
                      checked={addressForm.isBusiness}
                      onChange={e => setAddressForm({ ...addressForm, isBusiness: e.target.checked })}
                    />
                    🏢 Buying for business? (GST Invoice)
                  </label>

                  {addressForm.isBusiness && (
                    <div className="business-fields-subrow animate-slide-down">
                      <div className="input-group">
                        <label>Company / Registered Business Name *</label>
                        <input 
                          type="text" 
                          required={addressForm.isBusiness}
                          value={addressForm.companyName}
                          onChange={e => setAddressForm({ ...addressForm, companyName: e.target.value })}
                          placeholder="e.g. Acme Organic Exports"
                        />
                      </div>
                      <div className="input-group">
                        <label>GSTIN (Optional)</label>
                        <input 
                          type="text" 
                          value={addressForm.gstin}
                          onChange={e => setAddressForm({ ...addressForm, gstin: e.target.value.toUpperCase() })}
                          placeholder="e.g. 29AABCF8078M1ZX"
                          maxLength={15}
                        />
                      </div>
                    </div>
                  )}
                </div>

              </div>

              <footer className="modal-footer">
                <button type="button" className="modal-btn-cancel" onClick={() => setShowAddressModal(false)}>Cancel</button>
                <button type="submit" className="modal-btn-save" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Profile'}
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
